import { DOCUMENT } from "@angular/common";
import { Component, DestroyRef, ElementRef, computed, effect, inject, signal, untracked, viewChild } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { MatIconModule } from "@angular/material/icon";
import { filter, map } from "rxjs";
import { AgentChatService } from "../agent-chat/agent-chat.service";
import { ToastService } from "../common/services/toast.service";
import { WALKTHROUGH_QUERY_PARAM, anchorSelector, taskWalkthroughById, type WalkthroughSample, type WalkthroughSamples } from "./walkthrough-catalog";
import { fillSampleDates } from "./walkthrough-samples";
import { WalkthroughRunnerService } from "./walkthrough-runner.service";
import { WalkthroughTaskService } from "./walkthrough-task.service";

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Theme colors the finish confetti cycles through (tokens, so both themes look right). */
const CONFETTI_COLORS = [
  "var(--primary-color)",
  "var(--teal-color)",
  "var(--series-color)",
  "var(--discovery-color)",
  "var(--whatif-color)",
  "var(--success-color)",
];

/**
 * The finish burst: pieces fanned across the upper half-circle from the
 * burst point at the top of the card, then falling. Fixed (not random) so every finish looks the same and
 * tests stay stable; the spread comes from co-prime steps.
 */
const CONFETTI_PIECES = 34;
const CONFETTI = Array.from({ length: CONFETTI_PIECES }, (_, i) => {
  const angle = ((-172 + (i / (CONFETTI_PIECES - 1)) * 164 + ((i * 37) % 17) - 8) * Math.PI) / 180;
  const distance = 90 + ((i * 53) % 90);
  return {
    dx: `${Math.round(Math.cos(angle) * distance)}px`,
    dy: `${Math.round(Math.sin(angle) * distance)}px`,
    spin: `${((i * 67) % 540) - 270}deg`,
    delay: `${(i % 6) * 30}ms`,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    shape: (["strip", "square", "dot"] as const)[i % 3],
  };
});

/** The assistant panel's open sheet (agent-chat-sheet). */
const CHAT_SHEET = "tp-agent-chat-sheet .sheet";

const RING_PADDING = 6;
const POPOVER_GAP = 12;
const VIEWPORT_MARGIN = 8;
/** The docked popover's distance from the bottom-left corner (8 on a phone-width screen). */
const DOCK_MARGIN = 16;
const DOCK_MARGIN_NARROW = 8;
const NARROW_VIEWPORT = 480;

/**
 * The on-page half of a task walkthrough: a ring around the control the
 * current step is about and a small popover with the step text. Unlike a page
 * tour (Driver.js, modal), nothing here blocks the page — the ring ignores
 * pointer input and only dims the surroundings lightly, so the user performs
 * the step themselves and the assistant panel (which sits above the ring)
 * stays usable for questions.
 *
 * Material dialogs are the exception to plain z-index stacking: CDK 21 opens
 * overlays in the browser's top layer (the popover API), above every z-index.
 * While a step's control is inside one, the spotlight joins the top layer
 * after it (syncLayer), so the ring and popover stay above the dialog.
 *
 * The popover opens next to the control (below it, else above) and can be
 * dragged anywhere by its header when it covers something the user needs to
 * see; each step starts it back beside its control. When the user leaves the
 * walkthrough's pages (WalkthroughTaskService.strayed) the ring and popover
 * give way to a pause card: Continue goes back to where they were, Exit ends it.
 *
 * Mounted once at the app root. It also owns the `?walkthrough=<id>` deep
 * link, which starts a task walkthrough on whatever page it lands on.
 */
@Component({
  selector: "tp-walkthrough-spotlight",
  standalone: true,
  imports: [MatIconModule],
  template: `
    @if (task.active(); as active) {
      @if (task.strayed()) {
        <!-- Off the walkthrough's pages: nothing to ring here. The card is modal
             (its scrim takes the clicks) until the user picks Continue or Exit. -->
        <div class="wt-stray-scrim" aria-hidden="true"></div>
        <section class="wt-popover wt-stray" role="alertdialog" aria-live="assertive" aria-labelledby="wt-stray-title" aria-describedby="wt-stray-text">
          <span class="wt-progress">{{ active.entry.title }} · step {{ active.index + 1 }} of {{ active.entry.steps.length }}</span>
          <h3 class="wt-title" id="wt-stray-title">You've moved off the walkthrough.</h3>
          <p class="wt-text" id="wt-stray-text">If you'd like to finish click continue or just exit.</p>
          <footer class="wt-actions">
            <button type="button" class="wt-btn" (click)="task.exit()">Exit</button>
            <button type="button" class="wt-btn wt-btn--primary" (click)="task.continueAfterStray()">Continue</button>
          </footer>
        </section>
      } @else {
      @if (ring(); as box) {
        <div
          class="wt-ring"
          aria-hidden="true"
          [style.top.px]="box.top"
          [style.left.px]="box.left"
          [style.width.px]="box.width"
          [style.height.px]="box.height"
        ></div>
      }
      @if (task.currentStep(); as step) {
        <section
          #popover
          class="wt-popover"
          [class.wt-popover--docked]="!ring() && !dragOffset()"
          [class.wt-popover--dragging]="dragging()"
          role="dialog"
          aria-live="polite"
          [attr.aria-label]="active.entry.title"
          [style.top.px]="popoverPosition()?.top"
          [style.left.px]="popoverPosition()?.left"
        >
          <!-- The header is the drag handle: grab it to move the popover off whatever it covers. -->
          <header
            class="wt-head wt-drag-handle"
            title="Drag to move"
            (pointerdown)="onDragStart($event)"
            (pointermove)="onDragMove($event)"
            (pointerup)="onDragEnd($event)"
            (pointercancel)="onDragEnd($event)"
          >
            <mat-icon class="wt-grip" aria-hidden="true">drag_indicator</mat-icon>
            <span class="wt-progress">Step {{ active.index + 1 }} of {{ active.entry.steps.length }}</span>
            <button type="button" class="wt-exit" (click)="task.exit()" aria-label="Exit walkthrough">Exit</button>
          </header>
          <h3 class="wt-title">{{ step.title }}</h3>
          <p class="wt-text">{{ step.text }}</p>
          @if (currentPrompt(); as prompt) {
            <!-- Suggested messages for the assistant, one at a time: the user copies, pastes and sends one. -->
            <div class="wt-prompt">
              <em class="wt-prompt-text">{{ prompt.text }}</em>
              <div class="wt-prompt-bar">
                @if (prompt.of > 1) {
                  <button type="button" class="wt-prompt-nav" (click)="rotatePrompt(-1)" aria-label="Previous suggestion">
                    <mat-icon aria-hidden="true">chevron_left</mat-icon>
                  </button>
                  <span class="wt-prompt-count" aria-live="polite">{{ prompt.index + 1 }} of {{ prompt.of }}</span>
                  <button type="button" class="wt-prompt-nav" (click)="rotatePrompt(1)" aria-label="Next suggestion">
                    <mat-icon aria-hidden="true">chevron_right</mat-icon>
                  </button>
                }
                <button
                  type="button"
                  class="wt-prompt-copy"
                  (click)="copyPrompt(prompt.text)"
                  [attr.aria-label]="copied() ? 'Copied' : 'Copy this message'"
                  [title]="copied() ? 'Copied' : 'Copy'"
                >
                  <mat-icon aria-hidden="true">{{ copied() ? "check" : "content_copy" }}</mat-icon>
                  <span>{{ copied() ? "Copied" : "Copy" }}</span>
                </button>
              </div>
            </div>
          }
          @if (step.samples; as samples) {
            <!-- Test data on demand: one press fills the step's box with a sample. Nothing is sent or created. -->
            <div class="wt-samples">
              <p class="wt-samples-lead">{{ samples.lead }}</p>
              <div class="wt-samples-list">
                @for (choice of samples.choices; track choice.label) {
                  <button type="button" class="wt-sample" (click)="useSample(samples, choice)">{{ choice.label }}</button>
                }
              </div>
            </div>
          }
          @if (!ring()) {
            <p class="wt-missing">This control isn't on screen yet. It may need an earlier step, or a portfolio you can edit.</p>
          }
          <footer class="wt-actions">
            <!-- Back is free (off on the first step). Next shows only where it has a job:
                 a typed step (on once the box has text), or a step returned to with Back
                 whose result is still here. Click steps have no Next: the user does the action. -->
            <button type="button" class="wt-btn" [disabled]="!task.canGoBack()" (click)="task.back()">Back</button>
            @if (step.done?.kind === "manual") {
              <button type="button" class="wt-btn wt-btn--primary" (click)="task.completeManual()">Done</button>
            } @else if (task.showsNext()) {
              <button
                type="button"
                class="wt-btn"
                [disabled]="!task.canGoNext()"
                [attr.title]="task.canGoNext() ? null : task.onLastStep() ? 'The last step finishes the walkthrough' : 'Fill in the highlighted box first'"
                (click)="task.next()"
              >Next</button>
            }
          </footer>
        </section>
      }
      }
    } @else if (task.finished(); as entry) {
      <section class="wt-popover wt-popover--docked wt-finished" role="status" aria-live="polite">
        <!-- The burst starts at the card's top center. One burst per finish: the
             pieces are created with the card, so the animation runs once. -->
        <div class="wt-finished-burst" aria-hidden="true">
          <div class="wt-confetti">
            @for (piece of confetti; track $index) {
              <span
                class="wt-confetti-piece"
                [class]="'wt-confetti-piece wt-confetti-piece--' + piece.shape"
                [style.--dx]="piece.dx"
                [style.--dy]="piece.dy"
                [style.--spin]="piece.spin"
                [style.animation-delay]="piece.delay"
                [style.background]="piece.color"
              ></span>
            }
          </div>
        </div>
        <h3 class="wt-title wt-finished-title">{{ entry.title }} completed!</h3>
        @if (entry.finished) {
          <p class="wt-text">{{ entry.finished }}</p>
        }
        <footer class="wt-actions wt-finished-actions">
          <button type="button" class="wt-btn wt-btn--primary" (click)="task.dismissFinished()">Done</button>
        </footer>
      </section>
    }
  `,
  styleUrls: ["./walkthrough-spotlight.component.scss"],
})
export class WalkthroughSpotlightComponent {
  protected readonly task = inject(WalkthroughTaskService);
  protected readonly confetti = CONFETTI;
  /** The step's suggested prompt was just copied (the icon shows a check for a moment). */
  protected readonly copied = signal(false);
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;
  /** Which of the step's suggested messages is showing; back to the first on every step. */
  private readonly promptIndex = signal(0);
  protected readonly currentPrompt = computed(() => {
    const prompts = this.task.currentStep()?.prompts;
    if (!prompts?.length) return null;
    const index = ((this.promptIndex() % prompts.length) + prompts.length) % prompts.length;
    return { text: prompts[index], index, of: prompts.length };
  });
  private readonly tours = inject(WalkthroughRunnerService);
  private readonly chat = inject(AgentChatService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly document = inject(DOCUMENT);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly popover = viewChild<ElementRef<HTMLElement>>("popover");

  /** The ring's box in viewport coordinates, tracked every frame while a step has an anchor on screen. */
  protected readonly ring = signal<Box | null>(null);
  private readonly popoverSize = signal<{ width: number; height: number }>({ width: 320, height: 160 });
  /** The viewport, re-read every frame so the popover stays inside it through resizes. */
  private readonly viewport = signal<{ width: number; height: number }>({ width: 1024, height: 768 });
  /** How far the user dragged the popover from where it opens; null until they do, and again on every new step. */
  protected readonly dragOffset = signal<{ dx: number; dy: number } | null>(null);
  protected readonly dragging = signal(false);
  private drag: { pointerId: number; startX: number; startY: number; from: { dx: number; dy: number } } | null = null;
  private frame: number | null = null;
  /** The step we last scrolled into view / cleared the chat for, so each happens once per step. */
  private preparedStep: string | null = null;
  /** The overlay host (a dialog) the spotlight is raised above, or null while it uses plain z-index. */
  private raisedOver: Element | null = null;

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  /**
   * Where the popover opens: below the control when there is room, otherwise
   * above, always inside the viewport. Without a ring it is docked in the
   * bottom-left corner (by CSS until dragged). The user's drag offset is added
   * on top, clamped so the popover never leaves the viewport.
   */
  protected readonly popoverPosition = computed<{ top: number; left: number } | null>(() => {
    const box = this.ring();
    const offset = this.dragOffset();
    if (!box && !offset) return null;
    const size = this.popoverSize();
    const { width: viewportWidth, height: viewportHeight } = this.viewport();
    const maxLeft = Math.max(VIEWPORT_MARGIN, viewportWidth - size.width - VIEWPORT_MARGIN);
    const maxTop = Math.max(VIEWPORT_MARGIN, viewportHeight - size.height - VIEWPORT_MARGIN);
    let top: number;
    let left: number;
    if (box) {
      const below = box.top + box.height + POPOVER_GAP;
      const above = box.top - POPOVER_GAP - size.height;
      top = below + size.height <= viewportHeight - VIEWPORT_MARGIN ? below : above >= VIEWPORT_MARGIN ? above : maxTop;
      left = Math.min(Math.max(VIEWPORT_MARGIN, box.left), maxLeft);
    } else {
      const margin = viewportWidth < NARROW_VIEWPORT ? DOCK_MARGIN_NARROW : DOCK_MARGIN;
      top = viewportHeight - size.height - margin;
      left = margin;
    }
    if (offset) {
      top = Math.min(Math.max(VIEWPORT_MARGIN, top + offset.dy), maxTop);
      left = Math.min(Math.max(VIEWPORT_MARGIN, left + offset.dx), maxLeft);
    }
    return { top, left };
  });

  constructor() {
    // One guide at a time: a task walkthrough starting ends a running page tour.
    effect(() => {
      if (this.task.active()) untracked(() => this.tours.stop());
    });
    effect(() => {
      const tracking = this.task.active() !== null;
      untracked(() => (tracking ? this.startTracking() : this.stopTracking()));
    });
    effect(() => {
      const url = this.url();
      untracked(() => void this.startFromLink(url));
    });
    // A new step starts on its first suggestion, and back beside its control.
    effect(() => {
      this.task.currentStep();
      untracked(() => {
        this.promptIndex.set(0);
        this.copied.set(false);
        this.dragOffset.set(null);
        this.drag = null;
        this.dragging.set(false);
      });
    });
    inject(DestroyRef).onDestroy(() => this.stopTracking());
  }

  private startTracking(): void {
    if (this.frame !== null) return;
    const view = this.document.defaultView;
    if (!view) return;
    const tick = () => {
      this.measure();
      this.frame = view.requestAnimationFrame(tick);
    };
    this.frame = view.requestAnimationFrame(tick);
  }

  private stopTracking(): void {
    if (this.frame !== null) this.document.defaultView?.cancelAnimationFrame(this.frame);
    this.frame = null;
    this.preparedStep = null;
    this.ring.set(null);
    this.syncLayer(null);
  }

  /** Follow the anchor through scrolling, resizing and layout shifts; write signals only on change. */
  private measure(): void {
    const view = this.document.defaultView;
    if (view) {
      const viewport = this.viewport();
      if (view.innerWidth !== viewport.width || view.innerHeight !== viewport.height) {
        this.viewport.set({ width: view.innerWidth, height: view.innerHeight });
      }
    }
    const element = this.task.anchorElement();
    const rect = element?.getBoundingClientRect();
    const box: Box | null =
      rect && (rect.width > 0 || rect.height > 0)
        ? {
            top: Math.round(rect.top) - RING_PADDING,
            left: Math.round(rect.left) - RING_PADDING,
            width: Math.round(rect.width) + RING_PADDING * 2,
            height: Math.round(rect.height) + RING_PADDING * 2,
          }
        : null;
    const previous = this.ring();
    if (!sameBox(previous, box)) this.ring.set(box);
    // Above a dialog's overlay, or above the assistant panel when the step's
    // control is inside it (the panel out-ranks the spotlight by z-index).
    this.syncLayer(element?.closest(".cdk-overlay-popover") ?? element?.closest(CHAT_SHEET) ?? null);

    const popover = this.popover()?.nativeElement;
    if (popover) {
      const size = this.popoverSize();
      if (popover.offsetWidth && (popover.offsetWidth !== size.width || popover.offsetHeight !== size.height)) {
        this.popoverSize.set({ width: popover.offsetWidth, height: popover.offsetHeight });
      }
    }
    if (element && box) this.prepareStep(element, box);
  }

  /**
   * Join the top layer above the overlay the step's control lives in, or leave
   * it once the control is back on the page. Top-layer order is show order, so
   * re-showing after a newer overlay puts the spotlight back on top.
   */
  private syncLayer(overlay: Element | null): void {
    if (overlay === this.raisedOver) return;
    const host = this.host.nativeElement;
    if (typeof host.showPopover !== "function") return; // no top layer in this browser: z-index already wins
    if (this.raisedOver) host.hidePopover();
    this.raisedOver = overlay;
    if (overlay) {
      host.setAttribute("popover", "manual");
      host.showPopover();
    } else {
      host.removeAttribute("popover");
    }
  }

  /** Once per step: bring the control into view, and move the assistant panel out of its way. */
  private prepareStep(element: Element, box: Box): void {
    const active = this.task.active();
    const key = active ? `${active.entry.id}:${active.index}` : null;
    if (!key || key === this.preparedStep) return;
    this.preparedStep = key;
    const view = this.document.defaultView;
    const offscreen = !view || box.top < 0 || box.left < 0 || box.top + box.height > view.innerHeight || box.left + box.width > view.innerWidth;
    if (offscreen) element.scrollIntoView?.({ block: "center", inline: "nearest", behavior: "smooth" });
    // The panel floats above the ring. When it would hide the control the step
    // is about, close it — the conversation stays, one click away on the tab.
    // Not when the control IS in the panel (a step done in the chat).
    if (element.closest(CHAT_SHEET)) return;
    const sheet = this.document.querySelector(CHAT_SHEET)?.getBoundingClientRect();
    if (sheet && this.chat.isOpen() && !this.chat.fullScreen() && overlaps(sheet, box)) this.chat.close();
  }

  /** Grab the header (not its Exit button) to start moving the popover. */
  protected onDragStart(event: PointerEvent): void {
    if (event.button !== 0 || (event.target as Element | null)?.closest("button")) return;
    const handle = event.currentTarget as HTMLElement;
    handle.setPointerCapture?.(event.pointerId);
    this.drag = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, from: this.dragOffset() ?? { dx: 0, dy: 0 } };
    this.dragging.set(true);
    event.preventDefault();
  }

  protected onDragMove(event: PointerEvent): void {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    this.dragOffset.set({ dx: drag.from.dx + event.clientX - drag.startX, dy: drag.from.dy + event.clientY - drag.startY });
  }

  protected onDragEnd(event: PointerEvent): void {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    (event.currentTarget as HTMLElement).releasePointerCapture?.(event.pointerId);
    this.drag = null;
    this.dragging.set(false);
  }

  /** < and > rotate through the step's suggestions, wrapping at either end. */
  protected rotatePrompt(by: number): void {
    this.promptIndex.update((index) => index + by);
    this.copied.set(false);
  }

  /** Copy the step's suggested message; the user pastes and sends it themselves. */
  protected async copyPrompt(prompt: string): Promise<void> {
    try {
      await this.document.defaultView?.navigator.clipboard.writeText(prompt);
    } catch {
      this.toast.info("Couldn't copy. Select the text and copy it yourself.");
      return;
    }
    this.copied.set(true);
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
    this.copiedTimer = setTimeout(() => this.copied.set(false), 2000);
  }

  /**
   * A sample button: put the sample (dates filled in for today) into the
   * step's box, as if the user had pasted it, and leave the cursor there.
   * The box's own change handling takes it from here — the step completes
   * the way a paste would, and nothing is translated or created.
   */
  protected useSample(samples: WalkthroughSamples, choice: WalkthroughSample): void {
    const host = this.document.querySelector(anchorSelector(samples.into));
    const field = host?.matches("textarea, input") ? host : host?.querySelector("textarea, input");
    if (!(field instanceof HTMLTextAreaElement || field instanceof HTMLInputElement)) {
      this.toast.info("The box this sample goes into isn't on screen yet.");
      return;
    }
    field.value = fillSampleDates(choice.text);
    field.dispatchEvent(new Event("input", { bubbles: true }));
    field.dispatchEvent(new Event("change", { bubbles: true }));
    field.focus();
  }

  private async startFromLink(url: string): Promise<void> {
    const requested = this.router.parseUrl(url).queryParams[WALKTHROUGH_QUERY_PARAM];
    if (!requested) return;
    await this.router.navigate([], {
      queryParams: { [WALKTHROUGH_QUERY_PARAM]: null },
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
    const entry = taskWalkthroughById(String(requested));
    if (entry) this.task.start(entry);
    else this.toast.info("That walkthrough isn't available.");
  }
}

function sameBox(a: Box | null, b: Box | null): boolean {
  if (a === null || b === null) return a === b;
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

function overlaps(sheet: DOMRect, box: Box): boolean {
  return sheet.left < box.left + box.width && sheet.right > box.left && sheet.top < box.top + box.height && sheet.bottom > box.top;
}
